import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { allCaseStudies } from '../data/caseStudyData';

const CaseStudyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const caseStudy = allCaseStudies.find(item => item.id === Number(id));

  if (!caseStudy) {
    return (
      <div className="pt-40 pb-20 text-center">
        <h2 className="text-2xl font-bold">事例が見つかりませんでした。</h2>
        <Link to="/case-study" className="text-[#3aabd2] mt-4 inline-block">事例一覧に戻る</Link>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="bg-white py-16 text-center border-b">
        <div className="container mx-auto px-6">
          <h2 className="text-5xl font-extrabold text-[#3aabd2] mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>Case Study</h2>
          <p className="text-lg text-[#4b5563]" style={{ fontFamily: "'Inter', sans-serif" }}>事例紹介</p>
        </div>
      </header>
      
      {/* Summary Banner */}
      <div className="bg-[#3aabd2]/20 py-16">
        <div className="container mx-auto px-6 text-center">
          <div className="inline-block relative pb-6">
            <h3 
              className="text-xl md:text-3xl font-bold text-gray-800 leading-relaxed max-w-5xl mx-auto"
              dangerouslySetInnerHTML={{ __html: caseStudy.summary }}
            ></h3>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#3aabd2]"></div>
          </div>
        </div>
      </div>

      <section className="py-20">
        <div className="container mx-auto px-6 max-w-5xl mb-12">
          {/* Main Visual */}
          <div className="bg-white rounded-lg shadow-xl overflow-hidden border border-gray-200 mb-12 flex items-center justify-center mx-auto aspect-video max-w-4xl">
            <img src={caseStudy.img} alt={caseStudy.companyName} className="w-full h-full object-cover" />
          </div>

          {/* Company Info Card */}
          <div className="border-2 border-[#3aabd2] rounded-xl p-6 md:p-10 bg-white mb-8 mx-auto flex items-center text-lg shadow-lg w-full max-w-4xl">
            <div className="grid md:grid-cols-2 gap-y-6 gap-x-12 w-full">
              <div className="space-y-6 md:pl-5">
                <div className="flex items-start">
                  <span className="w-28 flex-shrink-0 text-gray-900 font-medium">会社名</span>
                  <span className="text-[#3aabd2] font-bold">{caseStudy.companyName}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-28 flex-shrink-0 text-gray-900 font-medium">設立</span>
                  <span className="text-gray-700">{caseStudy.details.establishment}</span>
                </div>
                <div className="flex items-start">
                  <span className="w-28 flex-shrink-0 text-gray-900 font-medium">研修期間</span>
                  <span className="text-gray-700">{caseStudy.details.trainingPeriod}</span>
                </div>
              </div>
              <div className="flex items-start">
                <span className="w-28 flex-shrink-0 text-gray-900 font-medium">事業内容</span>
                {/* ▼ ここを修正：事業内容の <br> を解釈できるように変更 */}
                <span 
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: caseStudy.details.businessDetail }}
                ></span>
              </div>
            </div>
          </div>

          {/* Problem and Solution */}
          <div className="border-2 border-[#3aabd2] rounded-xl p-8 md:p-12 bg-white mx-auto flex items-center text-lg shadow-lg w-full max-w-4xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8 w-full">
              {/* Problem */}
              <div className="flex-1 w-full">
                <div className="border border-[#3aabd2] rounded px-5 py-0.5 inline-block mb-4">
                  <span className="text-[#3aabd2] font-bold">課題</span>
                </div>
                {/* ▼ ここを修正 */}
                <p className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: caseStudy.details.problem }}></p>
              </div>

              {/* Arrow */}
              <div className="flex-shrink-0 flex items-center justify-center">
                <span className="text-[#3aabd2]/40 text-4xl hidden md:block">▶︎</span>
                <span className="text-[#3aabd2]/40 text-4xl md:hidden block">▼</span>
              </div>

              {/* Solution */}
              <div className="flex-1 w-full">
                <div className="bg-[#3aabd2] border border-[#3aabd2] rounded px-5 py-0.5 inline-block mb-4">
                  <span className="text-white font-bold">効果</span>
                </div>
                {/* ▼ ここを修正 */}
                <p className="text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: caseStudy.details.solution }}></p>
              </div>
            </div>
          </div>
        </div>

        {/* Interview Section - Full Width Backgrounds */}
        <div className="mb-16">
          {caseStudy.details.interviews.map((item, index) => (
            <div key={index} className={`${index % 2 === 0 ? 'bg-[#f9fafb]' : 'bg-white'} py-16 md:py-20`}>
              <div className="container mx-auto px-6 max-w-5xl">
                <h3 className="text-lg md:text-xl font-semibold text-gray-900 border-b border-gray-300 pb-2 mb-6 inline-block min-w-[60%]">
                  {item.question}
                </h3>
                {/* ▼ ここを修正：インタビュー回答の <br> を解釈できるように変更 */}
                <p 
                  className="text-gray-700 leading-relaxed text-base md:text-lg whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: item.answer }}
                ></p>
                {item.image && (
                  <div className="mt-8 flex justify-center">
                    <img 
                      src={item.image} 
                      alt="インタビュー写真" 
                      className="rounded-lg shadow-md w-full max-w-[277px] h-auto"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="container mx-auto px-6 max-w-5xl">
          {/* Footer Button */}
          <div className="text-center">
            <Link to="/case-study" className="inline-flex items-center justify-center py-3 px-8 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-[#3aabd2] hover:bg-[#2f8faf] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3aabd2] transition-all duration-300">
              事例紹介一覧に戻る
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CaseStudyDetailPage;